// Consumer side of the early fetches started by the inline script in
// public/index.html (perf: the app's first API call used to start ~2 s after
// navigation, because the JS bundle had to download, parse and render and auth
// had to resolve before any fetch began).
//
// The inline script kicks off one ANONYMOUS-SAFE request the moment HTML is
// parsed and stashes the promise on `window.__parcelyEarly`:
//
//   - GET /counties                      identical for every caller
//
// (A /properties map prefetch for logged-out visitors was removed: the landing
// page never reads it and it cost ~243 KB gz per visit.)
//
// It sends no Authorization or custom header, so there is no CORS preflight and
// no corsMiddleware change. The script never reads a token.
//
// Contract of this module:
//   - CONSUME ONCE. A take*() call removes the slot, so a result can never be
//     served twice (no stale data after a county switch / reload / sign-in).
//   - EXPIRES. A slot older than EARLY_MAX_AGE_MS is discarded, not served.
//   - NEVER WORSE THAN BEFORE. Every failure mode (script absent, fetch failed,
//     non-2xx, unparsable body, wrong shape, wrong county) yields `null`, and
//     the caller then issues its normal request exactly as it did before.

export const EARLY_MAX_AGE_MS = 30_000;

export interface EarlyResult {
  /** Parsed JSON body. */
  data: unknown;
  /** Raw `x-total-count` header, or null when absent / not exposed. */
  total: string | null;
  /** Raw `x-free-tier-limit` header, or null when absent / not exposed. */
  free: string | null;
}

interface EarlyHolder {
  /** Date.now() when the inline script started the requests. */
  t?: number;
  counties?: Promise<EarlyResult | null>;
}

declare global {
  interface Window {
    __parcelyEarly?: EarlyHolder;
  }
}

function freshHolder(now: number): EarlyHolder | null {
  if (typeof window === "undefined") return null;
  const h = window.__parcelyEarly;
  if (!h || typeof h.t !== "number") return null;
  if (now - h.t > EARLY_MAX_AGE_MS) return null;
  return h;
}

/**
 * The early /counties request, or null when there is none to use. Removes the
 * slot: it is served at most once. Resolves to null if the request failed.
 */
export function takeEarlyCounties(now: number = Date.now()): Promise<EarlyResult | null> | null {
  const h = freshHolder(now);
  const p = h?.counties;
  if (!h || !p) return null;
  delete h.counties;
  return p;
}
